import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useChat } from '../../contexts/ChatContext';
import { getApiBaseUrl } from '../../services/cloudApi';

/**
 * Modal to pick a friend/contact to share in chat.
 * Uses friends list from ChatContext; onSubmit(contactPayload).
 */
const ContactShareModal = ({ isOpen, onClose, onSubmit, excludeFriendId = null }) => {
  const { t } = useTranslation('chat');
  const { friends } = useChat();
  const [search, setSearch] = useState('');

  const filtered = friends
    .filter((f) => !f.isDeleted && (f.email || f.id) !== excludeFriendId)
    .filter((f) => {
      const name = (f.nickname || f.name || f.email || '').toLowerCase();
      const email = (f.email || '').toLowerCase();
      const q = search.trim().toLowerCase();
      return !q || name.includes(q) || email.includes(q);
    });

  const handleSelect = (friend) => {
    const userId = friend.user_id || friend.id;
    const name = friend.nickname || friend.name || friend.email || t('contactCard.unnamedContact');
    let avatarUrl = friend.avatarUrl || friend.avatar_url || null;
    if (avatarUrl && !avatarUrl.startsWith('http')) {
      try {
        const base = getApiBaseUrl();
        if (base && typeof base === 'string') {
          const baseUrl = base.replace('/api', '');
          avatarUrl = `${baseUrl}${avatarUrl.startsWith('/') ? '' : '/'}${avatarUrl}`;
        }
      } catch (_) {}
    }
    onSubmit({
      contact_user_id: userId,
      contact_name: name,
      contact_avatar_url: avatarUrl,
      contact_email: friend.email || null,
    });
    setSearch('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="relative w-full max-w-md max-h-[80vh] flex flex-col bg-[var(--color-bg-base)] dark:bg-[var(--color-bg-elevated)] rounded-2xl shadow-xl border border-white/10 dark:border-white/10 overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-4 border-b border-white/10 dark:border-white/10">
            <div className="flex items-center gap-2">
              <User className="w-5 h-5 text-[#3d8ef0]" />
              <h3 className="text-lg font-semibold text-[var(--color-text-primary)] dark:text-white">{t('contactShareModal.title')}</h3>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-full text-[var(--color-text-muted)] hover:bg-[var(--color-bg-elevated)] dark:hover:bg-[#24354f] hover:text-[var(--color-text-secondary)] dark:hover:text-gray-200"
              aria-label={t('common:close')}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="p-2 border-b border-white/10 dark:border-white/10">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('contactShareModal.searchPlaceholder')}
              className="w-full px-3 py-2 rounded-lg border border-white/10 dark:border-white/10 bg-[var(--color-bg-base)] dark:bg-[#161f31] text-[var(--color-text-primary)] dark:text-white placeholder-[var(--color-text-muted)] focus:ring-2 focus:ring-[#3d8ef0]/50 focus:border-transparent"
            />
          </div>
          <div className="flex-1 overflow-y-auto p-2">
            {filtered.length === 0 ? (
              <p className="text-sm text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] py-4 text-center">
                {t('contactShareModal.noFriends')}
              </p>
            ) : (
              filtered.map((friend) => {
                const name = friend.nickname || friend.name || friend.email || t('contactCard.unnamedContact');
                const email = friend.email || '';
                let avatarUrl = friend.avatarUrl || friend.avatar_url || null;
                if (avatarUrl && !avatarUrl.startsWith('http')) {
                  try {
                    const base = getApiBaseUrl();
                    if (base && typeof base === 'string') {
                      const baseUrl = base.replace('/api', '');
                      avatarUrl = `${baseUrl}${avatarUrl.startsWith('/') ? '' : '/'}${avatarUrl}`;
                    }
                  } catch (_) {}
                }
                const initials = name.slice(0, 2).toUpperCase();
                return (
                  <button
                    key={friend.email || friend.id}
                    type="button"
                    onClick={() => handleSelect(friend)}
                    className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-[var(--color-bg-elevated)] dark:hover:bg-[#24354f] transition-colors text-left"
                  >
                    {avatarUrl ? (
                      <img
                        src={avatarUrl}
                        alt=""
                        className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-[#2563eb]/10 dark:bg-[#2563eb]/10 flex items-center justify-center text-[#3d8ef0] dark:text-[#7ab8f8] font-medium flex-shrink-0">
                        {initials}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-[var(--color-text-primary)] dark:text-white truncate">
                        {name}
                      </p>
                      {email && (
                        <p className="text-xs text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] truncate">{email}</p>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ContactShareModal;
